'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import WhatsAppContactButton from '@/components/common/WhatsAppContactButton';

interface ServiceCardProps {
  title: string;
  description: string;
  image: string;
  alt: string;
  features: string[];
  responseTime: string;
  category: string;
}

export default function ServiceCard({
  title,
  description,
  image,
  alt,
  features,
  responseTime,
  category
}: ServiceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
          {category}
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-text-primary mb-2">{title}</h3>
        <p className="text-sm text-text-secondary mb-4">{description}</p>
        
        <div className="flex items-center gap-2 mb-4 text-sm">
          <Icon name="ClockIcon" size={16} variant="outline" className="text-success" />
          <span className="text-text-secondary">Response Time: <span className="font-semibold text-success">{responseTime}</span></span>
        </div>

        <div className="space-y-2 mb-4">
          {features.slice(0, isExpanded ? features.length : 3).map((feature, index) => (
            <div key={index} className="flex items-start gap-2">
              <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
              <span className="text-sm text-text-secondary">{feature}</span>
            </div>
          ))}
        </div>

        {features.length > 3 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary font-semibold hover:underline mb-4 flex items-center gap-1"
          >
            {isExpanded ? 'Show Less' : `Show ${features.length - 3} More`}
            <Icon 
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
              size={16} 
              variant="outline" 
            />
          </button>
        )}

        <div className="flex gap-2">
          <button className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:shadow-md transition-all duration-300 text-sm font-semibold flex items-center justify-center gap-2">
            <Icon name="DocumentTextIcon" size={16} variant="outline" />
            Get Quote
          </button>
          <WhatsAppContactButton 
            variant="inline"
            size="sm"
            className="flex-1"
            serviceName={title}
          />
        </div>
      </div>
    </div>
  );
}